/* Talking to the server. Every call carries the sign-in cookie. */

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, method: 'GET' | 'POST', body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body ? { 'content-type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    /*
     * This is what a screen shows when it could not fetch. It must not tell anyone to reach for the
     * paper chart: doses recorded from here on are saved on the tablet and sent when the signal is
     * back, and a carer who writes it on paper as well ends up with the dose recorded twice.
     */
    throw new ApiError(0, 'No connection. You can carry on — anything you record is saved on this tablet.');
  }

  const text = await response.text();
  const data = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!response.ok) {
    const message = typeof data.error === 'string' ? data.error : 'Something went wrong.';
    throw new ApiError(response.status, message);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, 'GET'),
  post: <T>(path: string, body?: unknown) => request<T>(path, 'POST', body),
};

/*
 * Recording something clinical, which has to survive the Wi-Fi dropping out mid-round.
 *
 * The record carries an id made here, before anything is sent, and the time it was recorded. If
 * there is no connection it goes into the outbox and is sent later with that same id, so a retry
 * can never become a second dose. Anything other than a lost connection — a refusal, a dose
 * somebody else has already recorded — is thrown, because the carer has to know now.
 */
export async function record<T>(
  path: string,
  body: Record<string, unknown>,
  what: string,
  id: string = crypto.randomUUID(),
): Promise<{ queued: boolean; result: T | null }> {
  const payload = { ...body, recordedAt: new Date().toISOString() };
  try {
    return { queued: false, result: await api.post<T>(path, payload) };
  } catch (err) {
    if (err instanceof ApiError && err.status === 0) {
      const { queue } = await import('./offline.ts');
      await queue({ id, url: path, body: payload, what });
      return { queued: true, result: null };
    }
    throw err;
  }
}

export type User = {
  id: string;
  name: string;
  initials: string;
  jobTitle: string;
  side: 'home' | 'pharmacy';
  roles: string[];
  homeIds: string[];
  agency: boolean;
  shiftEndsAt: string | null;
  mustSetPassword: boolean;
  competencyExpired: boolean;
};

export type Home = {
  id: string;
  name: string;
  address: string[];
  phone: string;
  supplyMode: 'tray' | 'original';
};

export type Round = {
  id: string;
  code: string;
  name: string;
  sealName: string;
  colour: string;
  startsAt: string;
  dueAt: string;
  endsAt: string;
};

export type RoundResident = {
  id: string;
  name: string;
  room: string;
  total: number;
  done: number;
  overdue: number;
  due: number;
  // Changes the home has reported for them that are not on the chart yet.
  reported?: number;
};

export type DoseRecord = {
  id: string;
  outcome: 'given' | 'not_given';
  code: string | null;
  codeLabel: string;
  note: string;
  recordedAt: string;
  recordedBy: string;
  witness: string;
  corrected: boolean;
  reason: string;
};

export type Dose = {
  key: string;
  medicineId: string;
  roundId: string | null;
  roundName: string;
  name: string;
  doseText: string;
  qty: number | null;
  slotNote: string;
  directions: string;
  route: string;
  dueOn: string;
  dueAt: string;
  timeCritical: boolean;
  controlledDrug: boolean;
  nurseOnly: boolean;
  topical: boolean;
  patch: boolean;
  insulin: boolean;
  warfarin: boolean;
  peg: boolean;
  trayType: 'tray' | 'separate' | 'none';
  trayLooks: string;
  notTrayReason: string;
  status: 'given' | 'not-given' | 'due' | 'overdue' | 'upcoming';
  late: boolean;
  record: DoseRecord | null;
  // A change the home has reported against this medicine that the pharmacy has not made yet.
  reported?: ReportedChange | null;
};

export type ReportedChange = {
  id: string;
  what: string;
  whatLabel: string;
  detail: string;
  medicineName: string;
  reportedBy: string;
  reportedAt: string;
  sourceDetail: string;
};

export type Witness = { id: string; name: string; jobTitle: string };

export type PrnMedicine = {
  medicineId: string;
  name: string;
  doseText: string;
  directions: string;
  reason: string;
  minGapMins: number | null;
  max24h: number | null;
  controlledDrug: boolean;
  nurseOnly: boolean;
  lastGivenAt: string | null;
  lastGivenBy: string;
  nextAllowedAt: string | null;
  minutesToWait: number;
  given24h: number;
  doseQty: number;
  wouldExceed: boolean;
  blockedReason: string | null;
  awaitingOutcome: Array<{ administrationId: string; givenAt: string; hoursAgo: number }>;
};

export type Resident = {
  id: string;
  homeId: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  title: string;
  dateOfBirth: string;
  room: string;
  status?: string;
  nhsNumber?: string;
  gpPractice?: string;
  allergies?: string[];
};
